import React, { useCallback, forwardRef, MutableRefObject } from 'react';
import { Pressable, Spinner, VStack, IButtonProps } from 'native-base';
import { Caption } from '~/shared/Typography';
import { useTheme, I18nReacher, I18nFormatArgs, useI18n } from '@springbig/shared';

type ButtonProps = IButtonProps & {
    text?: I18nReacher | string;
    textValues?: I18nFormatArgs;
    variant?: 'button' | 'neutral' | 'text' | 'danger';
    modifier?: 'normal' | 'small' | 'unlocked';
    children?: React.ReactNode;
    onPress?: () => void;
    underline?: boolean;
    disabled?: boolean;
    loading?: boolean;
};

export const Button = forwardRef(
    ({
        text,
        textValues,
        variant = 'button',
        modifier = 'normal',
        children,
        onPress,
        underline = false,
        disabled = false,
        loading = false,
        ...props
    }: ButtonProps, ref: MutableRefObject<HTMLDivElement>) => {

        if (children && text) {
            console.error('Button cannot have both a text property and a child node');
        }

        const { t } = useI18n();
        const isI18n = !!t(text);

        const {
            button_primary,
            button_text,
            radius_sm,
        } = useTheme();

        const style_background = {
            button: button_primary,
            neutral: button_primary?.slice(0, 7) + '80',
            text: null,
            danger: 'red',
        };

        const style_text = {
            button: button_text,
            neutral: button_text,
            text: button_primary,
            danger: 'white',
        };

        const style_border_size = {
            button: 1,
            neutral: 1,
            text: 1,
            danger: 1,
        };

        const style_border_color = {
            button: button_primary,
            neutral: button_primary,
            text: 'transparent',
            danger: 'red',
        };

        const padding_inline = {
            normal: 4,
            small: 1,
            unlocked: null,
        };

        const style_size = {
            normal: '40px',
            small: '28px',
            unlocked: null,
        };

        const style_spinner = {
            normal: 24,
            small: 16,
            unlocked: 24,
        };

        const renderButtonContent = useCallback(() => {

            return children ? (
                <VStack opacity={loading ? 0 : 1}>
                    { children }
                </VStack>
            ) : (
                <VStack w={'100%'} h={'100%'} px={padding_inline[modifier]} justifyContent={'center'}>
                    <Caption
                        textAlign={'center'}
                        color={props.color || style_text[variant]}
                        fontSize={props.fontSize}
                        opacity={loading ? 0 : 1}
                        underline={underline}
                        text={isI18n ? text : null}
                        textValues={textValues}
                    >
                        { isI18n ? null : text }
                    </Caption>
                </VStack>
            );
        }, [children, loading, props.color, style_text, variant, underline, text, textValues]);

        return (
            <Pressable
                onPress={onPress}
                disabled={disabled || loading}
                bg={style_background[variant]}
                opacity={disabled ? .5 : 1}
                borderWidth={style_border_size[variant]}
                borderColor={style_border_color[variant]}
                rounded={radius_sm}
                h={style_size[modifier]}
                {...props}
                ref={ref}
            >
                { loading &&
                <Spinner
                    position={'absolute'}
                    size={style_spinner[modifier]}
                    top={0}
                    bottom={0}
                    left={0}
                    right={0}
                    color={style_text[variant]}
                />
                }
                { renderButtonContent() }
            </Pressable>
        );
    });

Button.displayName = 'Button';

export default Button;
